import axios from 'axios'
import type { Response as Res } from '@/shared/types/response'
import type { Company, CompanyAddress, CompanyContact, CompanyData, CompanyInfo } from '@/features/companies/types'

interface CompanyFetched extends Omit<Company, 'id'> {
  companyId: string
}

const BASE_URL = import.meta.env.VITE_API_COMPANIES_URL

const companyServices = {
  async fetchCompanies(userId: string): Promise<Res<Company[]>> {
    try {
      const { data } = await axios.get<CompanyFetched[]>(`${BASE_URL}/api/v1/companies/me?userId=${userId}`)
      const noHasCompanies = data.length === 0
      const companies: Company[] = data.map(({ companyId, ...company }) => ({ ...company, id: companyId }))
      return noHasCompanies
        ? {
            data: null,
            ok: false,
            message: 'Usuário não está vinculado a nenhuma empresa',
          }
        : {
            data: companies,
            ok: true,
            message: 'Empresas carregadas com sucesso',
          }
    } catch (exception) {
      console.error('Error in fetchCompaniesService')
      return { data: null, ok: false, message: 'Erro ao carregar empresas' }
    }
  },

  async fetchCompanyData(companyId: string): Promise<Res<CompanyData>> {
    try {
      const endpoint = `${BASE_URL}/api/v1/companies/${companyId}`
      const { data } = await axios.get<CompanyData>(endpoint)
      return {
        ok: true,
        message: 'Dados da empresa carregado com sucesso',
        data: {
          address: data.address,
          cashbackPercentage: data.cashbackPercentage,
          commissionPercentage: data.commissionPercentage,
          companyType: data.companyType,
          cpfCnpj: data.cpfCnpj,
          createdAt: data.createdAt,
          discountPercentage: data.discountPercentage,
          email: data.email,
          id: data.id,
          mobilePhone: data.mobilePhone,
          name: data.name,
          personType: data.personType,
          tradeName: data.tradeName,
          updatedAt: data.updatedAt,
          description: data.description,
          phone: data.phone,
          site: data.site,
          slug: data?.slug,
        },
      }
    } catch (e) {
      console.error('Error in fetchCompanyData in companyServices')
      return { data: null, ok: false, message: 'Falha ao carregar dados da empresa' }
    }
  },

  async updateAddress(companyId: string, addressData: CompanyAddress): Promise<Res<CompanyAddress>> {
    try {
      const endpoint = `${BASE_URL}/api/v1/companies/${companyId}/address`
      const { data } = await axios.patch<{ address: CompanyAddress }>(endpoint, addressData)
      return { data: data.address, ok: true, message: 'Endereço da empresa atualizado com sucesso' }
    } catch (e) {
      console.error('Error in updateAddress in companyServices')
      return { data: null, ok: false, message: 'Falha ao atualizar endereço da empresa' }
    }
  },

  async updateInfo(companyId: string, infoData: CompanyInfo): Promise<Res<CompanyInfo>> {
    try {
      const endpoint = `${BASE_URL}/api/v1/companies/${companyId}/basic-info`
      await axios.patch(endpoint, infoData)
      return { data: infoData, ok: true, message: 'Dados da empresa atualizados com sucesso' }
    } catch (e) {
      console.error('Error in updateInfo in companyServices')
      return { data: null, ok: false, message: 'Falha ao atualizar dados da empresa' }
    }
  },

  async updateContact(companyId: string, contactData: CompanyContact): Promise<Res<CompanyContact>> {
    try {
      const endpoint = `${BASE_URL}/api/v1/companies/${companyId}/contact`
      await axios.patch(endpoint, contactData)
      return { data: contactData, ok: true, message: 'Dados de contato da empresa atualizados com sucesso' }
    } catch (e) {
      console.error('Error in updateContact in companyServices')
      return { data: null, ok: false, message: 'Falha ao atualizar dados de contato da empresa' }
    }
  },
}

export default companyServices
